/**
 * Campos auxiliares das medições: etapa do processo (process) e metadata.
 */

export const PROCESS_STAGES = [
  { id: "COLHEITA", label: "Colheita" },
  { id: "RECEBIMENTO", label: "Recebimento" },
  { id: "ARMAZENAGEM", label: "Armazenagem" },
  { id: "EXPEDICAO", label: "Expedição" },
];

const PROCESS_ID_ALIASES = {
  COLHEITA: "COLHEITA",
  RECEBIMENTO: "RECEBIMENTO",
  ARMAZENAGEM: "ARMAZENAGEM",
  ARMAZENAMENTO: "ARMAZENAGEM",
  EXPEDICAO: "EXPEDICAO",
};

const PROCESS_LABEL_BY_ID = Object.fromEntries(
  PROCESS_STAGES.map((stage) => [stage.id, stage.label])
);

export const METADATA_FIELD_LABELS = {
  observacao: "Observação",
  revisadoEm: "Revisado em",
  statusAnalise: "Status análise",
  loteCliente: "Lote cliente",
  revisadoPor: "Revisado por",
};

const stripAccents = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Normaliza o campo process para o id interno (COLHEITA, RECEBIMENTO, …). */
export const normalizeMeasureProcess = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const normalized = stripAccents(String(value).trim()).toUpperCase();
  if (PROCESS_ID_ALIASES[normalized]) {
    return PROCESS_ID_ALIASES[normalized];
  }
  if (PROCESS_LABEL_BY_ID[normalized]) {
    return normalized;
  }

  const byLabel = PROCESS_STAGES.find(
    (stage) =>
      stripAccents(stage.label).toUpperCase() === normalized ||
      stage.label.toLowerCase() === String(value).trim().toLowerCase()
  );
  return byLabel?.id || null;
};

export const getMeasureProcessLabel = (item) => {
  const raw = item?.process;
  if (!raw) return "--";
  const id = normalizeMeasureProcess(raw);
  return id ? PROCESS_LABEL_BY_ID[id] : String(raw);
};

export const getMeasureOperationId = (item) => normalizeMeasureProcess(item?.process);

export const getMeasureMetadata = (item) => {
  const meta = item?.metadata;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) {
    return {};
  }
  return meta;
};

export const hasMeasureMetadata = (item) => {
  const meta = getMeasureMetadata(item);
  return Object.keys(meta).some(
    (key) => formatMetadataValue(meta[key]) !== "--"
  );
};

export const buildMetadataTooltipLines = (item) => {
  if (!item) return [];

  const lines = [];
  const process = getMeasureProcessLabel(item);
  if (process && process !== "--") {
    lines.push(`Processo: ${process}`);
  }

  Object.entries(getMeasureMetadata(item)).forEach(([key, value]) => {
    const formatted = formatMetadataValue(value);
    if (formatted !== "--") {
      lines.push(`${metadataFieldLabel(key)}: ${formatted}`);
    }
  });

  return lines;
};

export const collectMetadataKeys = (dataList = []) => {
  const keys = new Set();
  (Array.isArray(dataList) ? dataList : []).forEach((item) => {
    Object.keys(getMeasureMetadata(item)).forEach((key) => keys.add(key));
  });
  return [...keys].sort();
};

export const metadataFieldLabel = (key) =>
  METADATA_FIELD_LABELS[key] ||
  String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

export const formatMetadataValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const collectUniqueProcesses = (dataList = []) => {
  const labels = new Set();
  (Array.isArray(dataList) ? dataList : []).forEach((item) => {
    const label = getMeasureProcessLabel(item);
    if (label && label !== "--") labels.add(label);
  });
  return [...labels];
};

export const collectMetadataSummary = (dataList = []) => {
  const keys = collectMetadataKeys(dataList);
  if (!keys.length) return [];

  return keys.map((key) => {
    const values = new Set();
    dataList.forEach((item) => {
      const formatted = formatMetadataValue(getMeasureMetadata(item)[key]);
      if (formatted !== "--") values.add(formatted);
    });
    return {
      key,
      label: metadataFieldLabel(key),
      values: [...values],
    };
  });
};

/**
 * Monta linhas para exportação Excel/CSV com processo e colunas dinâmicas de metadata.
 */
export const buildMeasureExportSheet = ({
  dataList = [],
  user,
  empresa,
  serialnumber,
  handleGetUnit,
  dateFormat,
}) => {
  const rows = Array.isArray(dataList) ? dataList : [];
  const hasDensity = rows.some(
    (item) => item.unidadePh && item.unidadePh.includes("Kg/m³")
  );
  const metadataKeys = collectMetadataKeys(rows);

  const headers = [
    "DATA DA MEDIDA",
    "TIPO DE GRÃO",
    "PROCESSO",
    "UMIDADE",
    hasDensity ? "DENSIDADE" : "PH",
    "TEMPERATURA DO GRÃO",
    "PESO",
    "EQUIPAMENTO",
    "LOCAL",
    "EMPRESA",
    "ID MED",
    ...metadataKeys.map(metadataFieldLabel),
  ];

  const mappedRows = rows.map((item) => {
    const base = {
      date: item?.date ? dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM") : "--",
      grain: item.grain || "--",
      process: getMeasureProcessLabel(item),
      humidity: item.humidity ? `${Number(item.humidity).toFixed(1)} %` : "--",
      unidadePh: item.unidadePh ? item.unidadePh : item.ph ? item.ph : "--",
      temperature: item.temperature ? Number(item.temperature).toFixed(1) : "--",
      peso:
        item.peso && item.peso !== "NaN"
          ? `${parseFloat(String(item.peso).replace(",", ".")).toFixed(2)} g`
          : "--",
      equipamento:
        item?.nameEquipament || item.serialnumber || serialnumber || "--",
      local: item?.localName || "--",
      company: user?.hideSensitiveInfo
        ? "*****"
        : item?.companyId && handleGetUnit
          ? handleGetUnit(item.companyId)
          : empresa || "--",
      idMed: item?.idMed || "--",
    };

    const metadata = getMeasureMetadata(item);
    metadataKeys.forEach((key) => {
      base[key] = formatMetadataValue(metadata[key]);
    });

    return base;
  });

  return { headers, mappedRows, hasDensity, metadataKeys };
};

const average = (values) =>
  values.length
    ? values.reduce((acc, val) => acc + val, 0) / values.length
    : null;

/** Linha de médias alinhada aos headers dinâmicos da planilha. */
export const buildMeasureAveragesRow = (headers = [], dataList = []) => {
  const row = new Array(headers.length).fill("");
  row[0] = "MÉDIAS";

  const humidityIdx = headers.indexOf("UMIDADE");
  const metricIdx = headers.findIndex(
    (header) => header === "PH" || header === "DENSIDADE"
  );
  const temperatureIdx = headers.indexOf("TEMPERATURA DO GRÃO");
  const pesoIdx = headers.indexOf("PESO");

  const humidityValues = dataList
    .map((item) => parseFloat(item.humidity))
    .filter((val) => !Number.isNaN(val));
  const phValues = dataList
    .map((item) => parseFloat(item.ph))
    .filter((val) => !Number.isNaN(val));
  const temperatureValues = dataList
    .map((item) => parseFloat(item.temperature))
    .filter((val) => !Number.isNaN(val));
  const pesoValues = dataList
    .map((item) => parseFloat(String(item.peso || "").replace(",", ".")))
    .filter((val) => !Number.isNaN(val));

  const humidityAvg = average(humidityValues);
  const phAvg = average(phValues);
  const temperatureAvg = average(temperatureValues);
  const pesoAvg = average(pesoValues);

  if (humidityIdx >= 0) {
    row[humidityIdx] = humidityAvg !== null ? `${humidityAvg.toFixed(1)} %` : "--";
  }
  if (metricIdx >= 0) {
    row[metricIdx] = phAvg !== null ? phAvg.toFixed(1) : "--";
  }
  if (temperatureIdx >= 0) {
    row[temperatureIdx] =
      temperatureAvg !== null ? `${temperatureAvg.toFixed(1)} °C` : "--";
  }
  if (pesoIdx >= 0) {
    row[pesoIdx] = pesoAvg !== null ? `${pesoAvg.toFixed(2)} g` : "--";
  }

  return row;
};
